import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { GetPromotionInfoListService } from './api-services/get-promotion-info-list.service';
import { PromotionInfoComboBoxModel } from './promotion-info-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 プロモーション情報コンボボックスを制御するクラス
 *
 * @export PromotionInfoComboBoxComponent
 * @class PromotionInfoComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'app-promotion-info-combo-box',
  templateUrl: 'promotion-info-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PromotionInfoComboBoxComponent,
      multi: true
    },
    {
      provide: GetPromotionInfoListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new GetPromotionInfoListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]
    }
  ],
})

export class PromotionInfoComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedPromotionNo: string = <string>'';
  public selectedPromotionInfo: string = <string>'';
  public selectedIndex: number = <number>-1;                                    // 選択した番号

  public model: PromotionInfoComboBoxModel = new PromotionInfoComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ

  constructor(
    public elementRef: ElementRef,
    public getPromotionInfoListService: GetPromotionInfoListService
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedPromotionNo) {
      this.selectedPromotionNo = text;
      this.setCodeValue(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期処理
   *
   * @memberof PromotionInfoComboBoxComponent
   */
  ngOnInit(): void {
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof PromotionInfoComboBoxComponent
   */
  ngAfterViewInit(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getPromotionInfoListService.execute(this.model);
    }
  }

  /**
   * コンポーネント終了時
   *
   * @memberof PromotionInfoComboBoxComponent
   */
  ngOnDestroy(): void {
    /** コンボボックス情報初期化 */
    this.clear();
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof PromotionInfoComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * クリア
   *
   * @memberof PromotionInfoComboBoxComponent
   */
  public clear(): void {
    this.selectedPromotionNo = null;
    this.selectedPromotionInfo = null;
    this.selectedIndex = <number>-1;
    this.model = new PromotionInfoComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @param {string} codeValue
   * @memberof PromotionInfoComboBoxComponent
   */
  public setCodeValue(codeValue: string): void {
    this.model.setCodeValue = codeValue;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedCodeValue();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof PromotionInfoComboBoxComponent
   */
  public updateSelectedCodeValue(): void {
    const codeList: Array<any> = this.model.codeList;
    for (let i = Number(0); i < codeList.length; i++) {
      if (codeList[i].PromotionNo === this.model.setCodeValue) {
        this.selectedIndex = i;
      }
    }
    if (this.afterFocus != null) {
      if (this.afterFocus instanceof HTMLElement) {
        this.afterFocus.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.afterFocus.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }
    }

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof PromotionInfoComboBoxComponent
   */
  public updateBindedField(e: any): void {
    if (e != null) {
      this.selectedIndex = e.target.selectedIndex;
    }
    if (this.selectedIndex === -1) {
      this.selectedPromotionNo = null;
      this.selectedPromotionInfo = null;

      return;
    }
    this.selectedPromotionNo = this.model.codeList[this.selectedIndex].PromotionNo;
    this.selectedPromotionInfo = this.model.codeList[this.selectedIndex].PromotionInfo;
  }

  /**
   * プロモーション情報リスト更新
   *
   * @memberof PromotionInfoComboBoxComponent
   */
  public updatePromotionInfo(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getPromotionInfoListService.execute(this.model);
    }
  }

}
